import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import bocaLogo from '../boca_logo.svg';

const MENU_STRINGS = {
  en: {
    ourMenu: 'Our Menu',
    noItems: 'No items available at this time.',
    loading: 'Loading menu...',
    lastUpdated: 'Last updated:',
    footer: '© 2026 BOCA POS',
  },
  es: {
    ourMenu: 'Nuestro Menú',
    noItems: 'No hay artículos disponibles en este momento.',
    loading: 'Cargando menú...',
    lastUpdated: 'Última actualización:',
    footer: '© 2026 BOCA POS',
  }
};

function MenuPage() {
  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoSrc, setLogoSrc] = useState(`${BACKEND_URL}/api/branding/logo`);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [lang, setLang] = useState('en');
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/menu`); // eslint-disable-line
        if (!res.ok) throw new Error('Failed to load menu');
        const data = await res.json();
        setMenuData(data);
        const expanded = {};
        (data.categories || []).forEach(cat => { expanded[cat.id] = true; });
        setExpandedCategories(expanded);
        if (data.appearance?.defaultLanguage) setLang(data.appearance.defaultLanguage);
      } catch (err) {
        setError('Unable to load menu. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  const appearance = menuData?.appearance || {};
  const fontFamily = appearance.font || 'system-ui, -apple-system, sans-serif';
  const bgColor = appearance.bgColor || '#f9f9f9';
  const textColor = appearance.textColor || '#1a1a1a';
  const headerColor = appearance.headerColor || '#333333';
  const accentColor = appearance.accentColor || '#1976d2';
  const accentOpacityHex = Math.round((appearance.accentOpacity ?? 7) * 2.55).toString(16).padStart(2, '0');
  const colsDesktop = parseInt(appearance.columns) || 1;
  const colsMobile = appearance.columnsMobile != null ? parseInt(appearance.columnsMobile) : Math.min(colsDesktop, 2);
  const colsSmall = appearance.columnsSmall != null ? parseInt(appearance.columnsSmall) : 1;
  const columns = vw <= 600 ? colsSmall : (vw <= 900 ? colsMobile : colsDesktop);
  const layout = appearance.layout || 'grid';
  const layoutThreshold = parseInt(appearance.layoutThreshold) || 5;
  const logoWidthMap = { small: 80, medium: 120, large: 180 };
  const logoWidth = appearance.logoSize === 'custom'
    ? (parseInt(appearance.logoCustomWidth) || 120)
    : (logoWidthMap[appearance.logoSize] || 120);
  const showLanguageToggle = appearance.languageToggle === true;
  const groups = appearance.groups || [];
  const showTitle = appearance.showTitle !== false;
  const showNames = appearance.showNames || 'both';
  const showGroupNames = showNames === 'both' || showNames === 'groups';
  const showCategoryNames = showNames === 'both' || showNames === 'categories';
  const s = MENU_STRINGS[lang] || MENU_STRINGS.en;

  // Load Google Font if needed
  useEffect(() => {
    if (!appearance.font) return;
    const fontName = appearance.font.replace(/['\"]/g, '').split(',')[0].trim();
    if (!fontName || fontName === 'system-ui' || fontName === 'sans-serif' || fontName === 'serif') return;
    const linkId = 'menu-google-font';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}&display=swap`;
      document.head.appendChild(link);
    }
  }, [appearance.font]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '1rem', backgroundColor: '#f9f9f9' }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 40, height: 40, border: '3px solid #e0e0e0', borderTop: '3px solid #333', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div>
        <p style={{ color: '#666' }}>{MENU_STRINGS[lang]?.loading || 'Loading menu...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem', backgroundColor: '#f9f9f9' }}>
        <p style={{ color: '#c62828', fontSize: '1.1rem', textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  const categories = menuData?.categories || [];
  const showItemImages = appearance.showItemImages !== false;
  const stackPhotos = vw <= 600; // small tier: stack image above text

  // Split a category's items into magazine-style featured cards (with image) and regular text rows.
  const splitItems = (items) => {
    const featured = items.filter(i => i.is_featured && i.image && showItemImages);
    const featuredIds = new Set(featured.map(i => i.id));
    const regular = items.filter(i => !featuredIds.has(i.id));
    return { featured, regular };
  };

  // Magazine-style card for a single featured item.
  const renderFeaturedItem = (item) => {
    const desc = lang === 'es' ? (item.description_es || item.description) : item.description;
    const imgSrc = `${BACKEND_URL}/api/products/${item.id}/image?v=${item.imageVersion || ''}`;
    return (
      <div key={`feat-${item.id}`} style={{
        display: 'flex',
        flexDirection: stackPhotos ? 'column' : 'row',
        gap: stackPhotos ? '0.5rem' : '1rem',
        padding: '0.875rem 1.1rem',
        borderBottom: `1px solid ${textColor}11`,
        alignItems: stackPhotos ? 'stretch' : 'center',
      }}>
        <div style={{
          flexShrink: 0,
          width: stackPhotos ? '100%' : 160,
          height: stackPhotos ? 200 : 160,
          borderRadius: 6,
          overflow: 'hidden',
          backgroundColor: `${textColor}08`,
        }}>
          <img src={imgSrc} alt={item.name} loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1 }}>
          <span style={{ fontSize: '1.05rem', fontWeight: '700', color: headerColor }}>{item.name}</span>
          {desc && <span style={{ fontSize: '0.85rem', color: `${textColor}aa`, lineHeight: 1.5 }}>{desc}</span>}
          <span style={{ fontSize: '1rem', fontWeight: '700', color: accentColor, marginTop: '0.2rem' }}>{formatPrice(item.price)}</span>
        </div>
      </div>
    );
  };

  // ── Shared category card renderer ──────────────────────────────────────────
  const renderCategoryCard = (category, extraStyle = {}) => {
    const { featured, regular } = splitItems(category.items);
    return (
      <div key={category.id} style={{
        backgroundColor: bgColor,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        ...extraStyle,
      }}>
        {showCategoryNames && (
          <div style={{ padding: '0.875rem 1.1rem', borderBottom: `2px solid ${accentColor}`, backgroundColor: `${accentColor}${accentOpacityHex}` }}>
            <span style={{ fontSize: '1rem', fontWeight: '700', color: headerColor }}>{lang === 'es' ? (category.name_es || category.name) : category.name}</span>
          </div>
        )}
        <div>
          {featured.map(renderFeaturedItem)}
          {regular.map((item, i) => (
            <div key={item.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '0.75rem 1.1rem',
              borderBottom: i < regular.length - 1 ? `1px solid ${textColor}11` : 'none',
              gap: '0.75rem',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1 }}>
                <span style={{ fontSize: '0.9rem', fontWeight: '500', color: textColor }}>{item.name}</span>
                {(lang === 'es' ? (item.description_es || item.description) : item.description) && <span style={{ fontSize: '0.78rem', color: `${textColor}77`, lineHeight: 1.4 }}>{lang === 'es' ? (item.description_es || item.description) : item.description}</span>}
              </div>
              <span style={{ fontSize: '0.9rem', fontWeight: '700', color: accentColor, whiteSpace: 'nowrap', flexShrink: 0 }}>{formatPrice(item.price)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Layout rendering ────────────────────────────────────────────────────────

  // Helper: render a subset of categories using the chosen layout
  const renderCategoriesInLayout = (cats) => {
    if (cats.length === 0) return null;
    if (columns === 1) {
      return cats.map(category => {
        const { featured, regular } = splitItems(category.items);
        return (
          <div key={category.id} style={{ marginBottom: '0.75rem', backgroundColor: bgColor, borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            {showCategoryNames && (
              <button style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left', borderBottom: expandedCategories[category.id] ? `2px solid ${accentColor}` : 'none' }}
                onClick={() => toggleCategory(category.id)} aria-expanded={!!expandedCategories[category.id]}>
                <span style={{ fontSize: '1rem', fontWeight: '700', color: headerColor }}>{lang === 'es' ? (category.name_es || category.name) : category.name}</span>
                <span style={{ fontSize: '0.72rem', color: `${textColor}66` }}>{expandedCategories[category.id] ? '▲' : '▼'}</span>
              </button>
            )}
            {(expandedCategories[category.id] || !showCategoryNames) && (
              <div>
                {featured.map(renderFeaturedItem)}
                {regular.map((item, i) => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.875rem 1.25rem', borderBottom: i < regular.length - 1 ? `1px solid ${textColor}11` : 'none', gap: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: '500', color: textColor }}>{item.name}</span>
                      {(lang === 'es' ? (item.description_es || item.description) : item.description) && <span style={{ fontSize: '0.8rem', color: `${textColor}77`, lineHeight: 1.4 }}>{lang === 'es' ? (item.description_es || item.description) : item.description}</span>}
                    </div>
                    <span style={{ fontSize: '0.95rem', fontWeight: '700', color: accentColor, whiteSpace: 'nowrap', flexShrink: 0 }}>{formatPrice(item.price)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      });
    } else if (layout === 'grid') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '1rem' }}>
          {cats.map(cat => renderCategoryCard(cat))}
        </div>
      );
    } else if (layout === 'flow') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '1rem' }}>
          {cats.map(cat => {
            const spans = cat.items.length >= layoutThreshold;
            return renderCategoryCard(cat, spans ? { gridColumn: `span ${columns}` } : {});
          })}
        </div>
      );
    } else if (layout === 'compact') {
      const colBuckets = Array.from({ length: columns }, () => []);
      const colCounts = Array(columns).fill(0);
      const sorted = [...cats].sort((a, b) => b.items.length - a.items.length);
      sorted.forEach(cat => {
        const minCol = colCounts.indexOf(Math.min(...colCounts));
        colBuckets[minCol].push(cat);
        colCounts[minCol] += cat.items.length;
      });
      return (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
          {colBuckets.map((bucket, ci) => (
            <div key={ci} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {bucket.map(cat => renderCategoryCard(cat))}
            </div>
          ))}
        </div>
      );
    } else {
      // Masonry
      return (
        <div style={{ columns, columnGap: '1rem', columnFill: 'balance' }}>
          {cats.map(cat => {
            const avoidBreak = cat.items.length < layoutThreshold;
            return renderCategoryCard(cat, {
              breakInside: avoidBreak ? 'avoid-column' : 'auto',
              display: 'inline-block',
              width: '100%',
              marginBottom: '1rem',
            });
          })}
        </div>
      );
    }
  };

  let menuContent;

  if (categories.length === 0) {
    menuContent = (
      <p style={{ textAlign: 'center', color: `${textColor}88`, padding: '3rem 1rem' }}>{s.noItems}</p>
    );
  } else if (groups.length > 0) {
    // Grouped rendering
    const groupIds = new Set(groups.map(g => g.id));
    const ungroupedCats = categories.filter(c => !c.groupId || !groupIds.has(c.groupId));
    menuContent = (
      <>
        {groups.map(group => {
          const groupCats = categories.filter(c => c.groupId === group.id);
          if (groupCats.length === 0) return null;
          return (
            <div key={group.id} style={{ marginBottom: '2rem' }}>
              {showGroupNames && (
                <h2 style={{
                  fontSize: '1.3rem', fontWeight: '700', color: headerColor,
                  margin: '0 0 0.75rem', paddingBottom: '0.4rem',
                  borderBottom: `2px solid ${accentColor}`,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {lang === 'es' ? (group.name_es || group.name) : group.name}
                </h2>
              )}
              {renderCategoriesInLayout(groupCats)}
            </div>
          );
        })}
        {ungroupedCats.length > 0 && renderCategoriesInLayout(ungroupedCats)}
      </>
    );
  } else {
    menuContent = renderCategoriesInLayout(categories);
  }

  return (
    <div className="menu-wrapper" style={{ minHeight: '100vh', backgroundColor: bgColor, fontFamily, color: textColor, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .menu-wrapper, .menu-wrapper * { box-sizing: border-box; }
        .menu-wrapper { overflow-x: hidden; }
        .menu-wrapper img { max-width: 100%; }
        @media (max-width: 600px) {
          .menu-wrapper { padding-left: 0.75rem; padding-right: 0.75rem; }
        }
      `}</style>

      {/* Header */}
      <header style={{ backgroundColor: bgColor, padding: '1.5rem 1rem 1rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
        {showLanguageToggle && (
          <div style={{ position: 'absolute', top: '1rem', right: '1rem', display: 'flex', gap: '0.25rem' }}>
            {['en', 'es'].map(l => (
              <button key={l} onClick={() => setLang(l)}
                style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: lang === l ? 'bold' : 'normal',
                  backgroundColor: lang === l ? accentColor : 'transparent',
                  color: lang === l ? '#fff' : accentColor,
                  border: `1px solid ${accentColor}`, borderRadius: 4, cursor: 'pointer', textTransform: 'uppercase' }}>
                {l}
              </button>
            ))}
          </div>
        )}
        <img src={logoSrc} alt="Restaurant Logo"
          style={{ width: logoWidth, height: 'auto', marginBottom: '0.25rem' }}
          onError={(e) => { e.target.onerror = null; e.target.src = bocaLogo; }} />
        {showTitle && (
          <p style={{ margin: 0, fontSize: '0.9rem', color: headerColor, fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {s.ourMenu}
          </p>
        )}
        {menuData?.mode === 'static' && menuData?.snapshotDate && (
          <p style={{ margin: 0, fontSize: '0.72rem', color: `${textColor}88` }}>
            {s.lastUpdated} {new Date(menuData.snapshotDate).toLocaleDateString()}
          </p>
        )}
      </header>

      {/* Menu Content */}
      <main className="menu-main" style={{ flex: 1, maxWidth: columns > 1 ? '1200px' : '720px', width: '100%', margin: '0 auto', padding: '1rem' }}>
        {menuContent}
      </main>

      <footer style={{ textAlign: 'center', padding: '1.5rem', borderTop: `1px solid ${textColor}11` }}>
        <small style={{ color: `${textColor}44`, fontSize: '0.72rem' }}>{s.footer}</small>
      </footer>
    </div>
  );
}

export default MenuPage;
